import type React from 'react';
import {
  createFileRoute,
  Outlet,
  redirect,
  retainSearchParams,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useAccountDetails } from '@/hooks/use-account-details';
import { hasPermission } from '@/lib/permissions';
import {
  accountDetailsOptions,
  aggregationByPropertyOptions,
  allEventsOptions,
  allEventsPrevOptions,
  dashboardConfigOptions,
  eventNamesOptions,
  infiniteEventsOptions,
  projectsOptions,
  usersOptions,
} from '@/hooks/query-options';
import {
  dashboardSearchSchema,
  dateRangeFromSearch,
  filtersFromSearch,
} from '@/lib/dashboard-search';
import {
  PAGEVIEW_WIDGETS,
  widgetToAggregationParams,
} from '@/lib/widget-aggregations';
import type { Widget as WidgetType } from '@/types';
import { AppSidebar, getActiveCategory } from '@/components/nav/app-sidebar';
import { NavBreadcrumb } from '@/components/nav/nav-breadcrumb';
import { ImpersonationBanner } from '@/components/super-admin/impersonation-banner';
import { DemoBanner } from '@/components/demo-banner';
import {
  FreePlanAnalyticsLock,
  FreePlanUsageBanner,
} from '@/components/free-plan-limit';
import { IngestionPausedBanner } from '@/components/ingestion-paused-banner';
import { TrialBanner } from '@/components/trial-banner';
import { PaymentFailedBanner } from '@/components/payment-failed-banner';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { PlanSheetHost, useOpenPlanSheet } from '@/components/onboarding/plan-sheet';
import { LaunchChecklistSheet } from '@/components/onboarding/launch-checklist-sheet';
import { TrialEndingDialog } from '@/components/onboarding/trial-ending-dialog';
import { useFeatures } from '@/hooks/use-features';
import type { FilterGroup } from '@/types';

export const Route = createFileRoute('/_auth/_side-layout')({
  validateSearch: dashboardSearchSchema,
  search: {
    // Persist dashboard selections (range, filters) across child routes so
    // dashboard ↔ events ↔ users ↔ queries navigations preserve the user's
    // view without every <Link> having to forward them manually.
    middlewares: [retainSearchParams(true)],
  },
  loaderDeps: ({ search }) => ({
    range: search.range,
    from: search.from,
    to: search.to,
    filters: search.filters,
  }),
  beforeLoad: async ({ context, location }) => {
    // Parent (_auth) blocks on accountDetailsOptions and projectsOptions, so
    // both are in the TanStack Query cache here; read them directly, no fetch.
    const accountData = context.queryClient.getQueryData(
      accountDetailsOptions().queryKey,
    );
    const projectsData = context.queryClient.getQueryData(
      projectsOptions().queryKey,
    );

    // A demo org's 'limited' anonymous visitors have no real account: keep them out of settings
    // (PII + writes). The demo org's owner/admins still manage it.
    const inDemo =
      accountData?.isDemo === true && context.userRole === 'limited';
    if (
      inDemo &&
      (location.pathname.startsWith('/settings') ||
        location.pathname.startsWith('/project-settings'))
    ) {
      throw redirect({ to: '/' });
    }

    // If _auth's prefetch failed, the cache is empty. Skip layout-specific
    // redirects and let component-level retries surface the error state.
    if (!accountData || !projectsData) {
      return;
    }

    if (projectsData.length === 0) {
      // A demo org with no project is half-provisioned; send the visitor to
      // /demo (shows unavailable), never the create-project onboarding wizard.
      if (accountData.isDemo) {
        throw redirect({ to: '/demo' });
      }
      throw redirect({ to: '/onboarding' });
    }

    // Stripe gave up collecting (unpaid): block everything but billing, where the
    // customer pays the open invoice or cancels. A past_due org just sees the banner.
    const sub = accountData.subscription;
    const unpaid =
      !!sub &&
      ['ESSENTIALS', 'PRO', 'ENTERPRISE'].includes(sub.plan) &&
      !sub.entitled;
    // Only owners can pay; other members stay in and see the payment banner.
    const canManageBilling = hasPermission(context.userRole, {
      billing: ['update'],
    });
    if (
      unpaid &&
      canManageBilling &&
      location.pathname !== '/settings/billing'
    ) {
      throw redirect({ to: '/settings/billing' as string });
    }
  },
  component: Layout,
  loader: async ({ context, deps }) => {
    const projectId = context.projectId;
    const projectsData = context.queryClient.getQueryData(
      projectsOptions().queryKey,
    );

    const dateRange = dateRangeFromSearch(deps);
    const filters = filtersFromSearch(deps);
    const filterGroups: FilterGroup[] = filters.length
      ? [
          {
            filters: filters.map((f) => ({
              fieldName: f.field,
              operator: f.operator,
              value: f.value,
            })),
          },
        ]
      : [{ filters: [] }];
    const selectedEvent = null;
    if (!Array.isArray(projectsData) || projectsData.length === 0) {
      context.projectId = null;
      context.setProjectId(null);
      return;
    }

    const hasValidProject = projectsData.some(
      (project: { projectId: string }) => project.projectId === projectId,
    );

    if (!projectId || !hasValidProject) {
      context.projectId = null;
      context.setProjectId(null);
      return;
    }

    context.queryClient.prefetchQuery(
      allEventsOptions({ projectId, dateRange, filterGroups }),
    );
    context.queryClient.prefetchQuery(
      allEventsPrevOptions({
        projectId,
        dateRange,
        filterGroups,
      }),
    );
    context.queryClient.prefetchInfiniteQuery(
      infiniteEventsOptions({
        projectId,
        filterGroups,
        selectedEvent,
      }),
    );
    context.queryClient.prefetchQuery(
      usersOptions({ projectId, page: 1 }),
    );
    context.queryClient.prefetchQuery(eventNamesOptions({ projectId }));

    // Resolve dashboard config first so we can fan out aggregation prefetches
    // for every widget that will render — built-in pageview widgets plus any
    // user-saved widgets. This eliminates the per-widget cold-cache flicker
    // (favicon pop-in, empty placeholder bars) by warming each
    // aggregationByPropertyOptions before its useSuspenseQuery mounts.
    const dashConfig = await context.queryClient.ensureQueryData(
      dashboardConfigOptions({ projectId }),
    );

    const userWidgets = dashConfig?.widgets ?? [];
    const allWidgets: (WidgetType | (typeof PAGEVIEW_WIDGETS)[number])[] = [
      ...PAGEVIEW_WIDGETS,
      ...userWidgets,
    ];
    for (const widget of allWidgets) {
      const params = widgetToAggregationParams(widget);
      if (!params) continue;
      context.queryClient.prefetchQuery(
        aggregationByPropertyOptions({
          projectId,
          dateRange,
          eventName: params.eventName,
          property: params.property,
          sumProperty: params.sumProperty,
          filterGroups,
        }),
      );
    }
  },
});

function Layout() {
  const {
    isOnTrial,
    isOnFreePlan,
    trialExpiresInDays,
    hasPaymentFailed,
    isSynthetic,
    introOfferEligible,
  } = useAccountDetails();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { userRole } = Route.useRouteContext();
  const isHome = getActiveCategory(pathname) === 'home';
  const { isEnabled } = useFeatures();
  const leanOnboarding = isEnabled('leanOnboarding');
  const openPlanSheet = useOpenPlanSheet();
  const canManageBilling = hasPermission(userRole, { billing: ['update'] });

  // Trial banner for trialing or free-plan users; a failed payment shows its own
  // banner instead. Demo orgs present as an established customer, never upsold.
  const showTrialBanner =
    (isOnTrial || isOnFreePlan) && !hasPaymentFailed && !isSynthetic;

  return (
    <SidebarProvider
      style={
        {
          '--sidebar-width': isHome ? '65px' : '304px',
          '--sidebar-width-icon': isHome ? '65px' : '130px',
        } as React.CSSProperties
      }
    >
      <AppSidebar />
      {/* overflow-hidden makes the inset a fixed viewport-height frame: without
          it, the inner scroll area's content escapes up through the flex chain
          (SidebarInset is flex-1 of a min-h-svh wrapper, neither of which clips)
          and creates a second, document-level scrollbar that drags the pinned
          footer along with it. Clipping here confines all scrolling to the
          inner overflow-auto region so the footer stays locked. */}
      <SidebarInset className="overflow-hidden">
        <div className="flex h-dvh flex-col">
          <ImpersonationBanner />
          <DemoBanner />
          <PaymentFailedBanner />
          {showTrialBanner && (
            <TrialBanner
              isTrialing={isOnTrial}
              trialDaysRemaining={trialExpiresInDays}
              isFreePlan={isOnFreePlan}
              onUpgrade={
                !canManageBilling
                  ? undefined
                  : leanOnboarding
                    ? () => openPlanSheet('trial_banner')
                    : () => navigate({ to: '/settings/billing' as string })
              }
              upgradeLabel={leanOnboarding ? 'Choose a Plan' : undefined}
              dataCreditsNote={leanOnboarding}
              introOffer={introOfferEligible}
            />
          )}
          <FreePlanUsageBanner />
          <IngestionPausedBanner />
          <NavBreadcrumb />
          <div className="min-h-0 grow overflow-auto">
            <FreePlanAnalyticsLock>
              <Outlet />
            </FreePlanAnalyticsLock>
          </div>
          <div className="flex h-9 shrink-0 grow-0 items-center justify-center border-t p-4 text-xs">
            &copy; {new Date().getFullYear()} Logspot | All Rights Reserved
          </div>
        </div>
      </SidebarInset>
      {leanOnboarding && (
        <>
          <PlanSheetHost />
          <LaunchChecklistSheet />
          {canManageBilling && !isSynthetic && <TrialEndingDialog />}
        </>
      )}
    </SidebarProvider>
  );
}
